import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  ExternalLink,
} from "lucide-react";

import { dashboardService, translationService } from "../services/api";
import NewsImageWithFallback from "../components/news/NewsImageWithFallback";
import { useTranslation } from "../contexts/TranslationContext";

// ─────────────────────────────────────────────────────────────────────────────
// Tokens / typography
// ─────────────────────────────────────────────────────────────────────────────
const TP = {
  blue:       "#4A90D9",
  blueStrong: "#2F78C4",
  blueSoft:   "#EBF5FF",
  ink:        "#1A1A2E",
  ink2:       "#6B7280",
  line:       "#E5E7EB",
  bgSoft:     "#F8FAFC",
};

const SCOPED_CSS = `
.tp-article {
  --tp-blue: ${TP.blue};
  --tp-blue-strong: ${TP.blueStrong};
  --tp-blue-soft: ${TP.blueSoft};
  --ink: ${TP.ink};
  --ink-2: ${TP.ink2};
  --line: ${TP.line};
  font-family: Inter, system-ui, sans-serif;
  color: var(--ink);
  padding-top: 24px;
}
@media (min-width: 768px) {
  .tp-article { padding-top: 32px; }
}
.tp-article .hero img {
  image-rendering: -webkit-optimize-contrast;
}
.tp-article .summary-banner {
  display: flex; gap: 12px; align-items: flex-start;
  margin: 24px 0; padding: 16px 18px;
  background: var(--tp-blue-soft);
  border: 1px solid #C7DEFA;
  border-radius: 10px;
  font-size: 14px; color: #1E3A6F;
  line-height: 1.5;
}
.tp-article .summary-banner .summary-cta {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 8px;
  font-weight: 700; color: var(--tp-blue-strong);
}
.tp-article .summary-banner .summary-cta:hover { color: var(--tp-blue); }
.tp-article a { color: inherit; text-decoration: none; }
.tp-article .crumbs { font-size: 13px; color: var(--ink-2); display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.tp-article .crumbs a { transition: color .15s; }
.tp-article .crumbs a:hover { color: var(--tp-blue); }
.tp-article .crumbs .sep { color: #CBD5E1; }
.tp-article .crumbs .current { color: #374151; }

/* Hero: NEVER crop. The box ADAPTS to the image: full container width,
   height follows the image's natural ratio up to a sensible cap (70vh) so a
   tall portrait never dominates the whole screen. The image is shown whole
   (object-fit:contain) on a neutral ground, so a portrait keeps its head and
   a wide photo is fully visible. It is intentional that this area can be
   taller than the old fixed 16:9 box. */
.tp-article .hero {
  position: relative;
  width: 100%;
  border-radius: 12px; overflow: hidden; background: #F1F3F5;
  box-shadow: 0 2px 8px rgba(0,0,0,.08);
}
/* Real hero image: width follows the column, height is intrinsic but capped
   to the viewport so it never gets absurdly tall; contain => no crop. The
   image is centered on the neutral ground if it is narrower than the column. */
.tp-article .hero > img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 70vh;
  object-fit: contain;
  object-position: center;
  margin: 0 auto;
}
/* Placeholder (missing/broken image): keep a stable 16:9 framed box so the
   layout does not collapse when there is no image to size the hero. */
.tp-article .hero--placeholder { aspect-ratio: 16/9; max-height: 480px; }
.tp-article .hero-gradient {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,.55) 100%);
}
.tp-article .cat-badge {
  position: absolute; top: 18px; left: 18px; z-index: 2;
  background: var(--tp-blue); color: #fff;
  font-size: 11.5px; font-weight: 700; letter-spacing: .12em;
  padding: 7px 12px; border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,.08);
}

.tp-article .article-head {
  padding: 36px 0 28px; border-bottom: 1px solid var(--line); margin-bottom: 32px;
}
.tp-article h1.tp-title {
  font-size: 42px; line-height: 1.15; font-weight: 800;
  letter-spacing: -.022em; margin: 0 0 14px;
}
.tp-article .tp-deck {
  font-size: 19px; line-height: 1.5; color: var(--ink-2);
  font-weight: 400; margin: 0 0 22px;
}
.tp-article .tp-meta {
  display: flex; align-items: center; flex-wrap: wrap; gap: 14px 18px;
  font-size: 13.5px; color: var(--ink-2);
}
.tp-article .tp-meta-item { display: inline-flex; align-items: center; gap: 6px; }
.tp-article .tp-author {
  display: inline-flex; align-items: center; gap: 9px;
  font-weight: 500; color: var(--ink);
}
.tp-article .tp-avatar {
  width: 28px; height: 28px; border-radius: 50%;
  background: linear-gradient(135deg, #A8C5E8, #4A90D9);
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; letter-spacing: .02em; flex-shrink: 0;
}
.tp-article .tp-dot { width: 3px; height: 3px; border-radius: 50%; background: #D1D5DB; }

.tp-article .source-row {
  display: flex; align-items: center; gap: 10px; margin-top: 16px; flex-wrap: wrap;
}
.tp-article .source-label {
  font-size: 12px; color: var(--ink-2); font-weight: 500;
  text-transform: uppercase; letter-spacing: .08em;
}
.tp-article .source-badge {
  display: inline-flex; align-items: center; gap: 9px;
  padding: 7px 12px 7px 8px; border-radius: 999px;
  background: #fff; border: 1px solid var(--line);
  font-size: 13.5px; font-weight: 600; color: var(--ink);
  box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06);
  transition: background .15s, border-color .15s, color .15s;
}
.tp-article .source-badge:hover {
  border-color: var(--tp-blue); background: var(--tp-blue-soft); color: var(--tp-blue-strong);
}
.tp-article .source-logo {
  width: 22px; height: 22px; border-radius: 5px;
  background: #111827; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 9.5px; font-weight: 800; letter-spacing: .02em;
  overflow: hidden; flex-shrink: 0;
}
.tp-article .source-logo img { width: 100%; height: 100%; object-fit: contain; }

/* Responsive frames — single source of truth for column width / padding.
   Body column narrows for readability; hero/related can be wider. */
.tp-article .tp-frame {
  width: 100%;
  margin-left: auto; margin-right: auto;
  box-sizing: border-box;
}
.tp-article .tp-frame--wide { max-width: 1200px; padding: 0 16px; }
.tp-article .tp-frame--body { max-width: 100%;   padding: 0 16px; }

/* Mobile defaults (< 640px) are already set above on tp-frame--body.
   Tablet+: progressively wider with tighter typography. */
@media (min-width: 640px) {
  .tp-article .tp-frame--wide { padding: 0 24px; }
  .tp-article .tp-frame--body { max-width: 90%; padding: 0 24px; }
}
@media (min-width: 1024px) {
  .tp-article .tp-frame--body { max-width: 760px; padding: 0 24px; }
}
@media (min-width: 1440px) {
  .tp-article .tp-frame--body { max-width: 820px; }
}

/* ─── Body typography ─── */
.tp-article .tp-body {
  font-family: "Source Serif 4", Charter, Georgia, serif;
  /* Mobile-first: 16px / 1.6 */
  font-size: 16px; line-height: 1.6; color: #202032;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
@media (min-width: 640px)  { .tp-article .tp-body { font-size: 17px; line-height: 1.65; } }
@media (min-width: 1024px) { .tp-article .tp-body { font-size: 18px; line-height: 1.7;  } }
@media (min-width: 1440px) { .tp-article .tp-body { font-size: 19px; line-height: 1.75; } }

.tp-article .tp-body p { margin: 0 0 20px; }

/* Drop cap — only on the first REAL body paragraph, which we tag with
   .tp-dropcap in JS (see markupForBody). We never rely on :first-of-type
   because the first <p> in provider HTML is often a caption, a "3 MIN READ"
   meta line, or a paragraph that starts with a link/number — none of which
   should get a drop cap. ::first-letter is also ignored by the browser when
   the paragraph starts with punctuation/quote, so this degrades gracefully. */
.tp-article .tp-body p.tp-dropcap::first-letter {
  float: left; font-weight: 700; font-size: 50px; line-height: .9;
  padding: 6px 10px 0 0; color: var(--tp-blue);
  font-family: Inter, system-ui, sans-serif;
}
@media (min-width: 1024px) {
  .tp-article .tp-body p.tp-dropcap::first-letter { font-size: 62px; }
}
.tp-article .tp-body a {
  color: var(--tp-blue-strong); text-decoration: underline;
  text-decoration-color: rgba(74,144,217,.35); text-underline-offset: 3px;
}
.tp-article .tp-body a:hover { text-decoration-color: var(--tp-blue); }
.tp-article .tp-body strong { color: var(--ink); }

.tp-article .tp-body blockquote {
  margin: 28px 0; padding: 6px 0 6px 20px;
  border-left: 4px solid var(--tp-blue);
  font-family: "Source Serif 4", Charter, Georgia, serif;
  font-size: 20px; line-height: 1.4; font-weight: 500; color: var(--ink);
  letter-spacing: -.005em;
}
@media (min-width: 1024px) {
  .tp-article .tp-body blockquote { font-size: 23px; padding-left: 24px; margin: 34px 0; }
}

/* In-article body images. NEVER hard-crop these: keep the natural aspect
   ratio (max-width:100% + height:auto) so wide images fill the column and
   tall/portrait images shrink to fit. A max-height (capped to the viewport)
   keeps very tall images from dominating the page; object-fit:contain on the
   capped height means a tall image is letterboxed onto a neutral ground
   rather than cut off. Centered, rounded, neutral bg for consistency. */
.tp-article .tp-body img {
  display: block;
  max-width: 100%;
  height: auto;
  max-height: min(80vh, 640px);
  object-fit: contain;
  object-position: center;
  margin: 24px auto;
  border-radius: 12px;
  background: #F1F3F5;
}
.tp-article .tp-body figure { margin: 24px 0; }
.tp-article .tp-body figcaption {
  font-family: Inter, system-ui, sans-serif;
  font-size: 13px; color: var(--ink-2); margin-top: 10px; line-height: 1.5;
}

/* Lists — Tailwind's preflight resets list-style:none on ul/ol globally,
   so provider lists (e.g. the 48-team ranking) render as an unstyled
   "Auflistung" with no markers. Re-assert markers + sensible indent,
   scoped to the article body only. */
.tp-article .tp-body ul,
.tp-article .tp-body ol {
  margin: 0 0 20px;
  padding-left: 28px;
  line-height: 1.5;
}
.tp-article .tp-body ul { list-style: disc outside; }
.tp-article .tp-body ol { list-style: decimal outside; }
.tp-article .tp-body li {
  margin: 0 0 8px;
  padding-left: 4px;
}
.tp-article .tp-body li::marker { color: var(--ink-2); }
.tp-article .tp-body ol > li::marker { font-weight: 600; color: var(--tp-blue-strong); }
/* Nested lists: tighter top spacing + alternate markers for hierarchy. */
.tp-article .tp-body ul ul,
.tp-article .tp-body ol ol,
.tp-article .tp-body ul ol,
.tp-article .tp-body ol ul { margin: 8px 0 0; }
.tp-article .tp-body ul ul { list-style-type: circle; }
.tp-article .tp-body ul ul ul { list-style-type: square; }
.tp-article .tp-body li > p { margin: 0 0 8px; }

/* Standalone caption blocks. Some providers emit image captions inline as
   a bare <div><p>…</p></div> (no <figure>), which otherwise reads as a
   broken body paragraph. We tag those in JS with .tp-caption and render
   them as a muted, italic caption. */
.tp-article .tp-body .tp-caption {
  margin: -10px 0 24px;
}
.tp-article .tp-body .tp-caption p {
  font-family: Inter, system-ui, sans-serif;
  font-size: 13.5px; line-height: 1.5; color: var(--ink-2);
  font-style: italic; margin: 0;
}

/* Tables — wrap in horizontal scroll on small screens.
   APITube articles occasionally embed wide stat tables. */
.tp-article .tp-body table {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  font-size: .95em;
  margin: 24px 0;
}
.tp-article .tp-body th,
.tp-article .tp-body td {
  border: 1px solid var(--line);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}
.tp-article .tp-body th { background: var(--bg-soft, #F8FAFC); font-weight: 600; }

.tp-article .tp-body h2 { font-family: Inter, system-ui, sans-serif; font-size: 22px; font-weight: 700; margin: 28px 0 12px; color: var(--ink); }
.tp-article .tp-body h3 { font-family: Inter, system-ui, sans-serif; font-size: 19px; font-weight: 700; margin: 24px 0 10px; color: var(--ink); }
@media (min-width: 1024px) {
  .tp-article .tp-body h2 { font-size: 26px; margin: 32px 0 14px; }
  .tp-article .tp-body h3 { font-size: 21px; margin: 28px 0 12px; }
}

.tp-article .tp-tags {
  margin: 36px 0 8px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.tp-article .tp-tags-label {
  font-size: 12px; color: var(--ink-2); font-weight: 600;
  text-transform: uppercase; letter-spacing: .08em; margin-right: 4px;
}
.tp-article .tp-tag {
  background: var(--tp-blue-soft); color: var(--tp-blue-strong);
  padding: 6px 12px; border-radius: 999px;
  font-size: 13px; font-weight: 500;
  border: 1px solid transparent;
  transition: background .15s, border-color .15s;
}
.tp-article .tp-tag:hover { background: #DCEBFC; border-color: rgba(74,144,217,.25); }

.tp-article .tp-related { margin: 64px 0 0; padding: 40px 0 0; border-top: 1px solid var(--line); }
.tp-article .tp-related-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 24px; gap: 16px; }
.tp-article .tp-related h2 { font-size: 22px; font-weight: 700; color: var(--ink); margin: 0; letter-spacing: -.01em; }
.tp-article .tp-related-link { font-size: 13.5px; font-weight: 600; color: var(--tp-blue); display: inline-flex; align-items: center; gap: 4px; transition: gap .15s; }
.tp-article .tp-related-link:hover { gap: 7px; }

.tp-article .tp-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.tp-article .tp-card {
  background: #fff; border-radius: 12px; overflow: hidden;
  border: 1px solid var(--line);
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s;
  display: flex; flex-direction: column;
}
.tp-article .tp-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(16,24,40,.10);
  border-color: transparent;
}
.tp-article .tp-card-body { padding: 14px 16px 16px; display: flex; flex-direction: column; gap: 8px; flex: 1; }
.tp-article .tp-card-cat { font-size: 10.5px; font-weight: 700; letter-spacing: .1em; color: var(--tp-blue); text-transform: uppercase; }
.tp-article .tp-card h3 {
  margin: 0; font-size: 15.5px; font-weight: 600; line-height: 1.35; color: var(--ink);
  letter-spacing: -.005em;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.tp-article .tp-card-meta {
  margin-top: auto; display: flex; align-items: center; gap: 8px;
  font-size: 12px; color: var(--ink-2); font-weight: 500;
}

@media (max-width: 980px) {
  .tp-article .tp-cards { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  /* On mobile the column is the full screen width; cap the hero a little
     lower than 70vh so a tall portrait still leaves the headline in view,
     while remaining fully uncropped (contain). */
  .tp-article .hero > img { max-height: 60vh; }
  .tp-article h1.tp-title { font-size: 28px; line-height: 1.18; }
  .tp-article .tp-deck { font-size: 16px; }
  .tp-article .tp-cards { grid-template-columns: 1fr; }
}
`;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return "";
  }
}

function initials(name) {
  if (!name) return "TP";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "TP";
}

function sourceAbbrev(source) {
  if (!source) return "";
  if (source.domain) {
    const root = source.domain.replace(/^www\./, "").split(".")[0] || "";
    return root.slice(0, 2).toUpperCase();
  }
  if (source.name) return initials(source.name).slice(0, 2);
  return "";
}

function truncate(s, n = 60) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// An author value is sometimes a malformed URL (e.g.
// "https://www. facebook. com/DailyMail"). Detect that so we can hide it
// rather than show a broken byline.
function looksLikeUrl(s) {
  if (!s) return false;
  const t = String(s).trim();
  return /^(https?:\/\/|www\.)/i.test(t) || /\.(com|net|org|io|co|news)\b/i.test(t.replace(/\s+/g, ""));
}

// A paragraph is eligible for the drop cap only if it's a real prose
// paragraph: it must start with a letter (not a digit, link, or quote),
// have enough text to carry an oversized initial, and not be a caption /
// meta line. Tightly conservative on purpose — better no drop cap than an
// ugly one.
function isDropCapEligible(p) {
  if (!p || p.tagName !== "P") return false;
  // Never inside a figure/figcaption or a caption div. We test the caption
  // div directly (not the .tp-caption class) so this is order-independent:
  // the class may not be applied yet when the drop-cap pass runs.
  if (p.closest("figure, figcaption, .tp-caption")) return false;
  if (isCaptionDiv(p.parentElement)) return false;
  // Must not lead with an element (link/strong/span) — drop-capping a link
  // looks broken and fights the run-together fix.
  const firstChild = p.firstChild;
  if (firstChild && firstChild.nodeType === 1) return false; // ELEMENT_NODE
  const text = (p.textContent || "").trim();
  if (text.length < 40) return false;          // too short -> probably a caption/meta
  if (!/^[A-Za-zÀ-ÿ]/.test(text)) return false; // must start with a letter
  return true;
}

// Does this element contain (or is) an image-bearing block? Used to detect
// the photo a caption belongs to.
function isImageBearing(el) {
  if (!el || el.nodeType !== 1) return false; // ELEMENT_NODE
  const tag = el.tagName;
  if (tag === "IMG" || tag === "FIGURE" || tag === "PICTURE") return true;
  return Boolean(el.querySelector && el.querySelector("img, picture"));
}

// A bare <div> that wraps a single <p> and nothing else is, in practice,
// an inline image caption that some providers emit without <figure>. Tag
// those so they render as captions instead of broken body paragraphs.
// Conservative: skip divs that also contain headings, lists, images, or
// multiple paragraphs (those are real content wrappers / FAQ blocks).
//
// A short single-<p> div on its own is ALSO a perfectly normal real body
// block, so the shape alone is not enough — we additionally require a
// neighbouring image (the photo the caption describes). Without that signal
// we leave the block as ordinary prose rather than italicising it.
function isCaptionDiv(div) {
  if (!div || div.tagName !== "DIV") return false;
  const els = Array.from(div.children);
  if (els.length !== 1) return false;
  const only = els[0];
  if (only.tagName !== "P") return false;
  if (only.querySelector("img, ol, ul, h1, h2, h3, h4, figure")) return false;
  const text = (only.textContent || "").trim();
  if (!text || text.length > 220) return false; // long => real paragraph, not a caption
  // Require an adjacent image: a caption sits right next to the photo it
  // describes (sibling before or after). A short prose paragraph standing on
  // its own has no such neighbour and stays unstyled.
  if (!isImageBearing(div.previousElementSibling) && !isImageBearing(div.nextElementSibling)) {
    return false;
  }
  return true;
}

// Post-process provider body HTML in the browser: tag the first real
// paragraph for the drop cap and mark standalone caption divs. Pure string
// in / string out so it composes with translation (which re-serialises the
// body too). Returns the original HTML unchanged if parsing yields nothing.
function decorateBodyHtml(html) {
  if (!html || typeof window === "undefined" || !window.DOMParser) return html;
  let doc;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return html;
  }
  const root = doc.body;
  if (!root) return html;

  // Caption divs FIRST, so the drop-cap pass below sees the .tp-caption
  // class via closest() and never drop-caps a caption paragraph.
  root.querySelectorAll("div").forEach((div) => {
    if (isCaptionDiv(div)) div.classList.add("tp-caption");
  });

  // Drop cap: first eligible paragraph in document order.
  const paras = root.querySelectorAll("p");
  for (const p of paras) {
    if (isDropCapEligible(p)) {
      p.classList.add("tp-dropcap");
      break;
    }
  }

  return root.innerHTML;
}

// Lazily load Source Serif 4 once when the page mounts.
function useSourceSerifFont() {
  useEffect(() => {
    const id = "tp-source-serif-font";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,600&display=swap";
    document.head.appendChild(link);
    // Don't remove on unmount — keep cached for navigation.
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────
function Breadcrumb({ title }) {
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      <Link to="/">Home</Link>
      <span className="sep">/</span>
      <Link to="/news">News</Link>
      <span className="sep">/</span>
      <span className="current">{truncate(title, 50)}</span>
    </nav>
  );
}

function Hero({ article }) {
  const categoryLabel = (article.category?.name || "TRANSFER NEWS").toUpperCase();
  const sourceName = article.source?.name;
  const [errored, setErrored] = useState(false);

  // Reset the error flag when navigating between articles, otherwise a dead
  // URL on one article would suppress a valid hero on the next.
  useEffect(() => { setErrored(false); }, [article.image]);

  const showImage = Boolean(article.image) && !errored;

  // When we HAVE a working image we render it directly (not via the shared
  // component) so the hero box can take the image's NATURAL height: contain +
  // max-height keeps the whole image visible and uncropped, letterboxed onto
  // the neutral ground for off-ratio sources. A portrait keeps its head; a
  // wide photo shows fully. No bottom gradient here: it only makes sense over
  // an edge-to-edge cover image, and a contained image must not be tinted.
  // When the image is missing OR fails to load we fall back to the shared
  // branded placeholder inside a stable 16:9 box so the layout never collapses.
  if (showImage) {
    return (
      <div className="relative">
        <div className="hero">
          <img
            src={article.image}
            alt={article.title || ""}
            onError={() => setErrored(true)}
          />
        </div>
        <span className="cat-badge">{categoryLabel}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <NewsImageWithFallback
        imageUrl={null}
        alt={article.title || ""}
        sourceName={sourceName}
        className="hero hero--placeholder w-full"
        rounded=""
      />
      <span className="cat-badge">{categoryLabel}</span>
    </div>
  );
}

function TitleBlock({ article }) {
  const rawAuthor = article.author?.name;
  // Hide bylines that are actually (malformed) URLs — show nothing rather
  // than "https://www. facebook. com/DailyMail". Source badge still carries
  // attribution.
  const authorName = looksLikeUrl(rawAuthor) ? null : rawAuthor;
  const source = article.source || {};
  return (
    <header className="article-head">
      <h1 className="tp-title">{article.title}</h1>
      {article.description && <p className="tp-deck">{article.description}</p>}
      <div className="tp-meta">
        {authorName && (
          <span className="tp-author">
            <span className="tp-avatar">{initials(authorName)}</span>
            {authorName}
          </span>
        )}
        {authorName && (article.published_at || article.read_time) && <span className="tp-dot" />}
        {article.published_at && (
          <span className="tp-meta-item">
            <Calendar size={14} /> {formatDate(article.published_at)}
          </span>
        )}
        {article.read_time && (
          <>
            <span className="tp-dot" />
            <span className="tp-meta-item">
              <Clock size={14} /> {article.read_time} min read
            </span>
          </>
        )}
      </div>
      {source.original_url && (
        <div className="source-row">
          <span className="source-label">Original Reporting</span>
          <a
            className="source-badge"
            href={source.original_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="source-logo">
              {source.favicon ? (
                <img src={source.favicon} alt="" />
              ) : (
                sourceAbbrev(source)
              )}
            </span>
            {source.name || source.domain || "External"}
            <ExternalLink size={13} style={{ opacity: 0.55 }} />
          </a>
        </div>
      )}
    </header>
  );
}

function SummaryBanner({ source }) {
  const sourceLabel = source?.name || source?.domain || "the publisher";
  return (
    <div className="summary-banner">
      <AlertIcon />
      <div>
        <div>
          You're reading a short summary: the news provider didn't share
          the full article body with us for this story.
        </div>
        {source?.original_url && (
          <a
            className="summary-cta"
            href={source.original_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Read the full story on {sourceLabel}
            <ExternalLink size={13} />
          </a>
        )}
      </div>
    </div>
  );
}

function AlertIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function Body({ html, isSummary, source }) {
  if (!html) {
    return (
      <div className="tp-body">
        <SummaryBanner source={source} />
      </div>
    );
  }
  // Backend sanitises via bleach — we trust the server-cleaned HTML.
  return (
    <article className="tp-body">
      {isSummary && <SummaryBanner source={source} />}
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </article>
  );
}

function Tags({ keywords }) {
  if (!keywords || keywords.length === 0) return null;
  return (
    <div className="tp-tags">
      <span className="tp-tags-label">Tags</span>
      {keywords.slice(0, 8).map((k) => (
        <span key={k} className="tp-tag">{k}</span>
      ))}
    </div>
  );
}

function RelatedCard({ article }) {
  return (
    <Link to={`/news/${article.id}`} className="tp-card" aria-label={article.title}>
      <NewsImageWithFallback
        imageUrl={article.image}
        alt={article.title || ""}
        sourceName={article.source}
        className="aspect-[16/10] w-full"
        rounded=""
      />
      <div className="tp-card-body">
        <span className="tp-card-cat">
          {(article.category || "NEWS").toString().toUpperCase()}
        </span>
        <h3>{article.title}</h3>
        <div className="tp-card-meta">
          {article.published_at && <span>{formatDate(article.published_at)}</span>}
          {article.published_at && article.source && <span className="tp-dot" />}
          {article.source && <span>{article.source}</span>}
        </div>
      </div>
    </Link>
  );
}

function Related({ items, loading }) {
  if (loading) {
    return (
      <section className="tp-related">
        <div className="tp-related-head">
          <h2>Read other news</h2>
        </div>
        <div className="tp-cards">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="tp-card animate-pulse" style={{ minHeight: 280 }}>
              <div className="aspect-[16/10] w-full bg-gray-200" />
              <div className="tp-card-body">
                <div className="h-3 w-16 bg-gray-200 rounded" />
                <div className="h-4 w-full bg-gray-200 rounded" />
                <div className="h-4 w-3/4 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }
  if (!items || items.length === 0) return null;
  return (
    <section className="tp-related">
      <div className="tp-related-head">
        <h2>Read other news</h2>
        <Link to="/news" className="tp-related-link">
          All news <ArrowRight size={14} />
        </Link>
      </div>
      <div className="tp-cards">
        {items.slice(0, 4).map((a) => (
          <RelatedCard key={a.id || a.url} article={a} />
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
const ArticleDetailPage = () => {
  useSourceSerifFont();
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { currentLanguage, translatePage } = useTranslation();
  const apiLanguage = (currentLanguage || "en").toLowerCase().split("-")[0];

  const [article, setArticle] = useState(null);
  const [translatedArticle, setTranslatedArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [translatedRelated, setTranslatedRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Scroll top on navigation between articles.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  useEffect(() => {
    if (!articleId) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);
    setArticle(null);

    // Both fetches need an explicit try/catch: axios throws CanceledError
    // when the abort signal fires (re-thrown by api.js), and an unhandled
    // promise rejection inside an async IIFE pops the CRA "Uncaught runtime
    // errors" overlay even though it's expected behaviour.
    (async () => {
      try {
        const res = await dashboardService.getArticleById(articleId, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (!res.success) {
          setError(res.status === 404 ? "not_found" : res.error || "Failed to load article");
          setLoading(false);
          return;
        }
        setArticle(res.data);
        setLoading(false);
      } catch (e) {
        if (e?.name === "CanceledError" || ctrl.signal.aborted) return;
        setError(e?.message || "Failed to load article");
        setLoading(false);
      }
    })();

    setRelatedLoading(true);
    setRelated([]);
    (async () => {
      try {
        const res = await dashboardService.getRelatedArticles(articleId, {
          signal: ctrl.signal,
          language: apiLanguage,
        });
        if (ctrl.signal.aborted) return;
        if (res.success) setRelated(res.data?.results || []);
      } catch (e) {
        if (e?.name === "CanceledError" || ctrl.signal.aborted) return;
        // Related is non-critical — just leave it empty on error.
      } finally {
        if (!ctrl.signal.aborted) setRelatedLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [articleId, apiLanguage]);

  // Translate the article content (title, description, body) directly via
  // the translation API. We bypass the global translation engine here
  // because it walks document.body looking for visible text nodes — and
  // body content rendered via dangerouslySetInnerHTML lands too late /
  // gets filtered out by the engine's visibility + path-failure guards,
  // so the body was staying in English while the rest of the UI changed.
  //
  // Approach: parse the body HTML into a DOM, collect every text node,
  // pack them all (plus title + description) into one batch call to
  // /users/translate/, then write the translations back into the DOM and
  // re-serialise. One round-trip, full article translated.
  useEffect(() => {
    if (!article) { setTranslatedArticle(null); return; }
    if ((apiLanguage || "en") === "en") { setTranslatedArticle(article); return; }

    let cancelled = false;
    // Show original while translation is in flight, swap when ready.
    setTranslatedArticle(article);

    (async () => {
      const texts = [];
      const slots = []; // { kind: 'title'|'description'|'body', node? }

      if (article.title) {
        slots.push({ kind: "title" });
        texts.push(article.title);
      }
      if (article.description) {
        slots.push({ kind: "description" });
        texts.push(article.description);
      }

      let doc = null;
      if (article.body_html) {
        doc = new DOMParser().parseFromString(article.body_html, "text/html");
        const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
          acceptNode: (n) => {
            const parent = n.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;
            const tag = parent.tagName.toLowerCase();
            if (["script", "style", "noscript"].includes(tag)) return NodeFilter.FILTER_REJECT;
            const txt = n.textContent.trim();
            if (!txt || txt.length < 2) return NodeFilter.FILTER_REJECT;
            if (/^\d+$/.test(txt)) return NodeFilter.FILTER_REJECT;
            if (/^[\w.-]+@[\w.-]+\.\w+$/.test(txt)) return NodeFilter.FILTER_REJECT;
            if (/^(https?:\/\/|www\.)/.test(txt)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        });
        let n;
        while ((n = walker.nextNode())) {
          slots.push({ kind: "body", node: n });
          texts.push(n.textContent);
        }
      }

      if (texts.length === 0) return;

      try {
        const res = await translationService.translateTexts(texts, apiLanguage);
        if (cancelled || !res.success || !Array.isArray(res.data)) return;

        let nextTitle = article.title;
        let nextDesc = article.description;

        res.data.forEach((tx, i) => {
          const slot = slots[i];
          if (!slot) return;
          const txText = typeof tx === "string" ? tx : (tx?.translated || tx?.translatedText || "");
          if (!txText) return;
          if (slot.kind === "title") nextTitle = txText;
          else if (slot.kind === "description") nextDesc = txText;
          else if (slot.kind === "body" && slot.node) {
            slot.node.textContent = txText;
          }
        });

        const nextBodyHtml = doc ? doc.body.innerHTML : article.body_html;

        if (!cancelled) {
          setTranslatedArticle({
            ...article,
            title: nextTitle,
            description: nextDesc,
            body_html: nextBodyHtml,
          });
        }
      } catch (e) {
        // Translation API failed — leave original showing, don't crash.
      }
    })();

    return () => { cancelled = true; };
  }, [article, apiLanguage]);

  // Related cards: translate the titles + meta fields the user sees.
  useEffect(() => {
    if (!related || related.length === 0) { setTranslatedRelated([]); return; }
    if ((apiLanguage || "en") === "en") { setTranslatedRelated(related); return; }

    let cancelled = false;
    setTranslatedRelated(related);

    (async () => {
      const texts = [];
      const slots = []; // { index, field }
      related.forEach((art, idx) => {
        if (art?.title) { slots.push({ index: idx, field: "title" }); texts.push(art.title); }
        if (art?.category) { slots.push({ index: idx, field: "category" }); texts.push(String(art.category)); }
      });
      if (texts.length === 0) return;

      try {
        const res = await translationService.translateTexts(texts, apiLanguage);
        if (cancelled || !res.success || !Array.isArray(res.data)) return;
        const out = related.map((a) => ({ ...a }));
        res.data.forEach((tx, i) => {
          const slot = slots[i];
          if (!slot) return;
          const txText = typeof tx === "string" ? tx : (tx?.translated || tx?.translatedText || "");
          if (!txText) return;
          out[slot.index][slot.field] = txText;
        });
        if (!cancelled) setTranslatedRelated(out);
      } catch (e) {
        // leave originals
      }
    })();

    return () => { cancelled = true; };
  }, [related, apiLanguage]);

  // Also nudge the global translation engine to re-pass our chrome (the
  // "Read other news", "All news", "Original Reporting", "min read", "Tags"
  // labels, error and loading copy). The engine handles those well — only
  // the article body was a problem, and that's covered above.
  useEffect(() => {
    if (!translatePage) return;
    if ((apiLanguage || "en") === "en") return;
    if (!translatedArticle && !error && !loading) return;
    const t = setTimeout(() => {
      const el = document.querySelector(".tp-article");
      if (el) translatePage(apiLanguage, false, el, { force: true });
    }, 150);
    return () => clearTimeout(t);
  }, [translatedArticle, translatedRelated, error, loading, apiLanguage, translatePage]);

  const breadcrumbTitle = useMemo(() => article?.title || "Article", [article]);

  const display = translatedArticle || article;
  // Tag the drop-cap paragraph + caption divs once per body. Memoised so
  // we don't re-parse on every render / unrelated state change.
  const decoratedBody = useMemo(
    () => decorateBodyHtml(display?.body_html),
    [display?.body_html]
  );

  if (loading) {
    return (
      <div className="tp-article">
        <style>{SCOPED_CSS}</style>
        <div className="tp-frame tp-frame--wide pt-5">
          <Breadcrumb title="Loading…" />
        </div>
        <div className="tp-frame tp-frame--wide mt-4">
          <div className="hero hero--placeholder animate-pulse" style={{ background: "#E5E7EB" }} />
        </div>
        <div className="tp-frame tp-frame--body mt-8">
          <div className="h-10 w-full bg-gray-200 rounded animate-pulse mb-4" />
          <div className="h-5 w-2/3 bg-gray-200 rounded animate-pulse mb-3" />
          <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
          <div className="mt-8 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-4 w-full bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tp-article">
        <style>{SCOPED_CSS}</style>
        <div className="tp-frame tp-frame--body py-16 text-center">
          <h1 className="tp-title" style={{ fontSize: 28 }}>
            {error === "not_found" ? "Article not found" : "Couldn't load this article"}
          </h1>
          <p className="tp-deck mt-3">
            {error === "not_found"
              ? "This story has been removed or its ID is invalid."
              : "Please try again in a moment."}
          </p>
          <div className="mt-6 inline-flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/news")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg"
              style={{ background: TP.blue, color: "#fff", fontWeight: 600 }}
            >
              <ArrowLeft size={16} /> Back to news
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tp-article">
      <style>{SCOPED_CSS}</style>

      <div className="tp-frame tp-frame--wide pt-5">
        <Breadcrumb title={display?.title || breadcrumbTitle} />
      </div>

      <div className="tp-frame tp-frame--wide mt-4">
        <Hero article={display} />
      </div>

      <div className="tp-frame tp-frame--body">
        <TitleBlock article={display} />
        <Body
          html={decoratedBody}
          isSummary={Boolean(display.body_is_summary)}
          source={display.source}
        />
        <Tags keywords={display.keywords} />
      </div>

      <div className="tp-frame tp-frame--wide pb-16">
        <Related items={translatedRelated.length ? translatedRelated : related} loading={relatedLoading} />
      </div>
    </div>
  );
};

export default ArticleDetailPage;
